import {naturalSort} from '@benchling/js-utils';
import React from 'react';

import {
  NO_USER,
  StatusTypeToDisplayNameMapping,
  Worksheet,
  WorksheetTaskStatusType,
} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';
import {usePMEIsAndMethodTasks} from 'procedures/worksheets/ProcedureWorksheetContext';
import getTestProps from 'test/lib/cypress/getTestProps';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {formalTimeUTC} from 'util/DateUtil';

import {extractProcedureRunDataFromTask} from './WorksheetUtils';

interface ProcedureWorksheetPrinterFriendlyHeaderProps {
  worksheet: Worksheet;
}

const extractDisplayValue = (value: string | number | undefined | null): string =>
  value != null ? `${value}` : '-';

function ConditionAndReplicateLabel(): JSX.Element {
  const {pmeisAndTasks: pmeisAndMethodTasks} = usePMEIsAndMethodTasks();
  if (!pmeisAndMethodTasks || pmeisAndMethodTasks.length === 0) {
    return <></>;
  } else if (pmeisAndMethodTasks.length === 1) {
    const {conditionName, replicateNumber} = extractProcedureRunDataFromTask(
      pmeisAndMethodTasks[0].task,
      pmeisAndMethodTasks[0].pmei
    );
    return (
      <>
        <p className="valign">
          <b>Condition:</b>
          <span className="margin-left-0_5em">{extractDisplayValue(conditionName)}</span>
        </p>
        <p className="valign">
          <b>Replicate:</b>
          <span className="margin-left-0_5em">{extractDisplayValue(replicateNumber)}</span>
        </p>
      </>
    );
  } else {
    const labels = naturalSort(
      pmeisAndMethodTasks.map(({task, pmei}) => {
        const {conditionName, replicateNumber} = extractProcedureRunDataFromTask(task, pmei);
        return `${extractDisplayValue(conditionName)} ${extractDisplayValue(replicateNumber)}`;
      })
    );
    return (
      <p className="valign">
        <b>Condition Replicates:</b>
        <span className="margin-left-0_5em">{labels.join(', ')}</span>
      </p>
    );
  }
}

export default function ProcedureWorksheetPrinterFriendlyHeader({
  worksheet,
}: ProcedureWorksheetPrinterFriendlyHeaderProps): JSX.Element {
  const {
    worksheetTaskData: {status},
  } = WorksheetContext.useContext();
  const {pmeisAndTasks: pmeisAndMethodTasks} = usePMEIsAndMethodTasks();
  const {unitOperationName, runStarted, runEnded} = extractProcedureRunDataFromTask(
    pmeisAndMethodTasks[0]?.task,
    pmeisAndMethodTasks[0]?.pmei
  );
  const study = worksheet?.studies?.[0]?.__typename === 'StudyNode' ? worksheet.studies[0] : null;

  return (
    <div {...getTestProps({component: 'ProcedureWorksheetPrinterFriendlyHeader', element: 'root'})}>
      <div className="title">
        <h1 {...getTestProps({component: 'ProcedureWorksheetPrinterFriendlyHeader', element: 'name'})}>
          {worksheet?.name}
        </h1>
        <hr />
      </div>
      <div
        className="section"
        {...getTestProps({component: 'ProcedureWorksheetPrinterFriendlyHeader', element: 'metadata'})}
      >
        <p className="valign">
          <b>Experiment:</b>
          <span className="margin-left-0_5em">{study?.name || '-'}</span>
        </p>
        <p className="valign">
          <b>Unit operation:</b>
          <span className="margin-left-0_5em">{unitOperationName || '-'}</span>
        </p>
        <ConditionAndReplicateLabel />
        <div className="inline-flex-center">
          <b>Run started:</b>
          {runStarted ? (
            <>
              <span className="margin-left-0_5em">{formalTimeUTC(runStarted.timestamp)}&nbsp;</span>
              <div className="inline-flex-center">
                <Avatar entity={runStarted.creator ?? NO_USER} size={AvatarSize.XXSMALL} />
              </div>
              <span className="margin-left-0_5em">{runStarted.creator?.name}</span>
            </>
          ) : (
            <span className="margin-left-0_5em">-</span>
          )}
        </div>
        <p className="valign">
          <b>Run state:</b>
          <span className="margin-left-0_5em">
            {StatusTypeToDisplayNameMapping[status ?? WorksheetTaskStatusType.PENDING]}
          </span>
        </p>
        <div className="inline-flex-center">
          <b>Run finalized:</b>
          {runEnded ? (
            <>
              <span className="margin-left-0_5em">{formalTimeUTC(runEnded.timestamp)}&nbsp;</span>
              <div className="inline-flex-center">
                <Avatar entity={runEnded.creator ?? NO_USER} size={AvatarSize.XXSMALL} />
              </div>
              <span className="margin-left-0_5em">{runEnded.creator?.name}</span>
            </>
          ) : (
            <span className="margin-left-0_5em">-</span>
          )}
        </div>
      </div>
    </div>
  );
}
